// src/components/pages/EditProfile.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getColors } from '../../styles/colors';
import { 
  FaArrowLeft, FaUser, FaPhone, FaMapMarkerAlt, 
  FaCamera, FaSave, FaTimes, FaSpinner, FaCheck,
  FaTrash
} from 'react-icons/fa';
import { updateProfile, getProfile, clearError } from '../../redux/slices/authSlice';

const EditProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  const { user, isLoading, error } = useSelector((state) => state.auth);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    profile_image: null
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      console.log('📦 Setting form data from user:', user);
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        address: user.address || '',
        profile_image: user.profile_image || null
      });
      
      // Set preview image if exists
      if (user.profile_image) {
        const imageUrl = getImageUrl(user.profile_image);
        setPreviewImage(imageUrl);
      }
    }
  }, [user]);

  // ✅ FIXED - Get correct image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    
    // If path already starts with 'uploads/', don't add it again
    if (imagePath.startsWith('uploads/')) {
      return `https://admin.thefoodies.in/public/${imagePath}`;
    }
    
    // If path starts with 'profile_images/', add the full path
    if (imagePath.startsWith('profile_images/')) {
      return `https://admin.thefoodies.in/public/uploads/${imagePath}`;
    }
    
    // Default: assume it's just the filename in profile_images folder
    return `https://admin.thefoodies.in/public/uploads/profile_images/${imagePath}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file (JPEG, PNG, GIF)');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      
      setFormData(prev => ({ ...prev, profile_image: file }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
      setImageError(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, profile_image: null }));
    setPreviewImage(null);
    setImageError(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setSuccessMessage('');
    dispatch(clearError());
    
    try {
      const formDataToSend = new FormData();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('phone', formData.phone);
      if (formData.address) {
        formDataToSend.append('address', formData.address);
      }
      
      if (formData.profile_image && typeof formData.profile_image === 'object' && formData.profile_image instanceof File) {
        formDataToSend.append('profile_image', formData.profile_image);
        console.log('📸 Uploading new image:', formData.profile_image.name);
      }
      
      if (formData.profile_image === null && user?.profile_image) {
        formDataToSend.append('remove_image', 'true');
        console.log('📸 Removing image');
      }
      
      console.log('📤 Submitting profile update with FormData');
      
      const result = await dispatch(updateProfile(formDataToSend)).unwrap();
      console.log('✅ Profile updated successfully:', result);
      
      setSuccessMessage('Profile updated successfully!');
      
      await dispatch(getProfile()).unwrap();
      
      setTimeout(() => {
        navigate('/profile');
      }, 1500);
      
    } catch (err) {
      console.error('❌ Failed to update profile:', err);
      alert(typeof err === 'string' ? err : 'Failed to update profile. Please check your inputs and try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    navigate('/profile');
  };

  const displayImageUrl = previewImage;

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="relative pt-6 pb-4 px-4 border-b"
           style={{
             background: `linear-gradient(180deg, ${colors.primary}20 0%, ${colors.background} 100%)`,
             borderColor: colors.border
           }}>
        <div className="flex items-center justify-between">
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 px-4 py-2 rounded-full transition-all hover:scale-105"
            style={{ 
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`,
              color: colors.text
            }}
          >
            <FaArrowLeft />
            <span>Back</span>
          </button>
          <h1 className="text-xl font-bold" style={{ color: colors.text }}>
            Edit Profile
          </h1>
          <div style={{ width: '80px' }}></div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {successMessage && (
          <div className="mb-4 p-3 rounded-xl flex items-center gap-2"
               style={{ 
                 backgroundColor: '#22C55E20',
                 color: '#22C55E',
                 border: '1px solid #22C55E'
               }}>
            <FaCheck />
            <span>{successMessage}</span>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 rounded-xl flex items-start gap-2"
               style={{ 
                 backgroundColor: '#FEF2F2',
                 color: '#DC2626',
                 border: '1px solid #FECACA'
               }}>
            <span className="mt-0.5">⚠️</span>
            <span>{error}</span>
            <button
              onClick={() => dispatch(clearError())}
              className="ml-auto text-xs font-medium hover:underline"
              style={{ color: '#DC2626' }}
            >
              Dismiss
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} encType="multipart/form-data">
          <div className="rounded-2xl p-6" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
            {/* Profile Image */}
            <div className="flex flex-col items-center mb-6">
              <div className="relative">
                <div className="w-32 h-32 rounded-full overflow-hidden border-4" 
                     style={{ borderColor: colors.primary }}>
                  {displayImageUrl && !imageError ? (
                    <img
                      src={displayImageUrl}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      onError={handleImageError}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"
                         style={{ backgroundColor: colors.inputBackground }}>
                      <FaUser className="text-5xl" style={{ color: colors.textSecondary }} />
                    </div>
                  )}
                </div>
                
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 w-10 h-10 rounded-full flex items-center justify-center text-white transition-all hover:scale-110"
                  style={{ backgroundColor: colors.primary }}
                >
                  <FaCamera className="text-sm" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
                
                {displayImageUrl && (
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute bottom-0 left-0 w-10 h-10 rounded-full flex items-center justify-center text-white transition-all hover:scale-110"
                    style={{ backgroundColor: '#EF4444' }}
                  >
                    <FaTrash className="text-sm" />
                  </button>
                )}
              </div>
              <p className="text-xs mt-2" style={{ color: colors.textSecondary }}>
                Tap camera icon to upload profile photo
              </p>
              <p className="text-xs" style={{ color: colors.textSecondary }}>
                Max size: 5MB (JPEG, PNG, GIF)
              </p>
            </div>

            {/* Name */}
            <div className="mb-4">
              <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                <FaUser className="inline mr-2" />
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full p-3 rounded-xl focus:outline-none transition-all"
                style={{ 
                  backgroundColor: colors.inputBackground,
                  border: `1px solid ${colors.border}`,
                  color: colors.text
                }}
                placeholder="Enter your full name"
                required
              />
            </div>

            {/* Phone */}
            <div className="mb-4">
              <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                <FaPhone className="inline mr-2" />
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full p-3 rounded-xl focus:outline-none transition-all"
                style={{ 
                  backgroundColor: colors.inputBackground,
                  border: `1px solid ${colors.border}`,
                  color: colors.text
                }}
                placeholder="Enter your phone number"
                required
              />
            </div>

            {/* Address */}
            <div className="mb-6">
              <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                <FaMapMarkerAlt className="inline mr-2" />
                Delivery Address
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
                className="w-full p-3 rounded-xl focus:outline-none resize-none transition-all"
                style={{ 
                  backgroundColor: colors.inputBackground,
                  border: `1px solid ${colors.border}`,
                  color: colors.text
                }}
                placeholder="Enter your delivery address"
              />
              <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>
                This will be used for delivering your orders
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={uploading || isLoading}
                className="flex-1 py-3 rounded-xl font-semibold text-white transition-all hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ 
                  backgroundColor: colors.primary,
                  boxShadow: `0 4px 20px ${colors.primary}40`
                }}
              >
                {uploading || isLoading ? (
                  <><FaSpinner className="animate-spin" /> Saving...</>
                ) : (
                  <><FaSave /> Save Profile</>
                )}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-3 rounded-xl font-medium transition-all hover:scale-105"
                style={{ 
                  backgroundColor: colors.inputBackground,
                  color: colors.text,
                  border: `1px solid ${colors.border}`
                }}
              >
                <FaTimes /> Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfile;